import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { PanelHeader, ChartSelector } from 'js/components';
import { Breadcrumbs } from 'js/components/Breadcrumbs';
import LoadingMessage from 'js/components/Loading/LoadingMessage';
import { useChartSearchParam } from 'js/hooks/useChartSearchParam';
import AssessmentPicker from './AssessmentPicker';
import OrgDetailTabs from './OrgDetailTabs';
import { TreeGlyph, TreeGlyphRectangles } from './Glyphs';
import OrgTree from './Visualizations/OrgTree';
import RubricOrgTree from './Visualizations/RubricOrgTree';
import OrgAssessmentHistoryChart from './Visualizations/OrgAssessmentHistory';
import OrgAssessmentDetailContainer from '../../containers/OrgAssessmentDetailContainer';
import Participation from './Visualizations/Participation';
import SendRemindersModal from 'js/routes/Organizations/components/OrgComponent/SendRemindersModal';
import { rubricDocumentToOrgTemplate } from 'js/utils/rubric-org-scores';
import { resolveOrgDetailViews } from 'js/utils/org-detail-views';
import { Glyph as RadialPieGlyph } from 'js/engines/SurveyAssessment/components/Review/AssessmentHistoryRadialPieChart';
import { Glyph as RadarGlyph } from 'js/components/Charts/SeriesRadarChart';
import OrgAssessmentRadarChart from './Visualizations/OrgAssessmentRadarChart';

const OrgComponent = ({
  organization,
  assessmentHistorySummaries,
  templates,
  loading,
  error,
  workspace,
  history,
  teamScoresQuery,
  teamParticipationQuery,
  assessmentKey,
  isRubric = false,
  orgRubricScoresQuery,
  questionScoresQuery,
  match,
  onSendReminders,
}) => {
  const [showConfirmSendReminders, setShowConfirmReminders] = useState(false);
  const [reminderData, setReminderData] = useState({
    orgName: '',
    assessmentIds: [],
  });
  const location = useLocation();
  const [chartKey, setChartKey] = useChartSearchParam();

  const confirmSendReminders = useCallback(
    (orgName, assessmentIds) => {
      setReminderData({ orgName, assessmentIds });
      setShowConfirmReminders(true);
    },
    [setReminderData, setShowConfirmReminders]
  );

  const handleSendReminders = useCallback(
    (assessmentIds, threshold) => {
      return (async () => {
        await onSendReminders(assessmentIds, threshold);
        setShowConfirmReminders(false);
      })();
    },
    [setShowConfirmReminders, onSendReminders]
  );

  const assessmentTemplate = useMemo(() => {
    if (isRubric) {
      if (orgRubricScoresQuery?.data) {
        const { rubric_id, rubric_name, document } = orgRubricScoresQuery.data;
        return rubricDocumentToOrgTemplate(rubric_id, rubric_name, document);
      }

      const summary = assessmentHistorySummaries.find(
        (s) => s.assessment_key === assessmentKey
      );

      return {
        key: assessmentKey,
        type: 'rubric',
        name: summary?.assessment_name ?? 'Rubric',
        views: {
          orgDetail: ['recommendations', 'squad-scorecard', 'org-trends'],
        },
        assessment: { question_sets: [] },
      };
    }

    return templates?.find(({ key }) => key === assessmentKey);
  }, [
    isRubric,
    orgRubricScoresQuery?.data,
    templates,
    assessmentKey,
    assessmentHistorySummaries,
  ]);

  const detailViewConfig = useMemo(() => {
    const configured = assessmentTemplate?.views?.orgDetail ?? [];
    const summary = assessmentHistorySummaries.find(
      (s) => s.assessment_key === assessmentKey
    );

    return resolveOrgDetailViews(configured, {
      isRubric,
      templateType: assessmentTemplate?.type,
      assessmentType: summary?.assessment_type,
    });
  }, [assessmentTemplate, assessmentHistorySummaries, assessmentKey, isRubric]);

  const orgCharts = useMemo(() => {
    const radialPieChart = {
      key: 'radial-pie',
      label: RadialPieGlyph,
      component: OrgAssessmentHistoryChart,
      buttonProps: { 'data-chart-type': 'radial-pie' },
      componentProps: {
        organization,
        assessmentTemplate,
        questionScores: questionScoresQuery?.data,
        catalog: questionScoresQuery?.catalog,
        loading: questionScoresQuery?.query?.isLoading ?? false,
        error: questionScoresQuery?.query?.error,
      },
    };

    const radarChart = {
      key: 'radar',
      label: RadarGlyph,
      component: OrgAssessmentRadarChart,
      buttonProps: { 'data-chart-type': 'radar' },
      componentProps: {
        organization,
        assessmentTemplate,
        questionScores: questionScoresQuery?.data,
        catalog: questionScoresQuery?.catalog,
        orgRubricScores: orgRubricScoresQuery?.data,
        assessmentKey,
        orgId: match.params.orgId,
        isRubric,
      },
    };

    const treeChart = {
      key: 'tree',
      label: TreeGlyph,
      component: OrgTree,
      buttonProps: { 'data-chart-type': 'tree' },
      componentProps: {
        organization,
        assessmentKey,
        workspaceId: workspace?.id,
        teamScores: teamScoresQuery.data,
        loading: teamScoresQuery.query.isLoading,
        error: teamScoresQuery.query.error,
      },
    };

    if (isRubric) {
      return [
        {
          key: 'tree',
          label: TreeGlyph,
          component: RubricOrgTree,
          buttonProps: { 'data-chart-type': 'tree' },
          componentProps: {
            organization,
            rubricId: assessmentKey,
            rubricTeams: orgRubricScoresQuery?.data?.teams,
            rubricDocument: orgRubricScoresQuery?.data?.document,
            workspaceId: workspace?.id,
            teamScores: teamScoresQuery.data,
            loading: teamScoresQuery.query.isLoading,
            error: teamScoresQuery.query.error,
          },
        },
        radialPieChart,
        radarChart,
      ];
    }

    return [
      treeChart,
      {
        key: 'participation',
        label: TreeGlyphRectangles,
        component: Participation,
        buttonProps: { 'data-chart-type': 'participation' },
        componentProps: {
          organization,
          teamParticipation: teamParticipationQuery.data,
          loading: teamParticipationQuery.query.isLoading,
          error: teamParticipationQuery.query.error,
          workspaceId: workspace.id,
          assessmentKey,
          onSendReminders: confirmSendReminders,
        },
      },
      radialPieChart,
      radarChart,
    ];
  }, [
    isRubric,
    organization,
    assessmentKey,
    assessmentTemplate,
    workspace?.id,
    teamScoresQuery,
    questionScoresQuery,
    orgRubricScoresQuery?.data,
    match.params.orgId,
    teamParticipationQuery,
    confirmSendReminders,
  ]);

  if (!templates && !isRubric) {
    return null;
  }

  return (
    <div>
      <PanelHeader size="flex">
        <div className="panel-content assessment-review-header pb-0">
          <div className="container-fluid">
            <Breadcrumbs
              breadcrumbs={[
                {
                  label: 'Organizations',
                  to: `/w/${workspace?.id ?? match.params.workspaceId}/orgs/`,
                },
              ]}
            />
            <section className="details flex">
              <div className="details-main">
                {loading ? (
                  <div className="mt-3">
                    <LoadingMessage iconSize="2x">
                      Loading &hellip;
                    </LoadingMessage>
                  </div>
                ) : error ? (
                  <p className="mt-3 danger">
                    Uh oh, there was a problem loading the organization
                  </p>
                ) : (
                  <h1>
                    {organization.id === 0 ? workspace.name : organization.name}
                  </h1>
                )}
              </div>
              <div className="details-sidebar">
                {loading ? null : (
                  <AssessmentPicker
                    assessmentHistorySummaries={assessmentHistorySummaries}
                    defaultActiveKey={assessmentKey}
                    onChoose={(key) => {
                      history.push(
                        `/w/${workspace.id}/orgs/${organization.id}/${key}`
                      );
                    }}
                  />
                )}
              </div>
            </section>
          </div>
        </div>
        <div
          className="container-fluid org-chart-selector"
          style={{ marginBottom: '2rem' }}
        >
          {organization && (
            <ChartSelector
              loading={
                !teamScoresQuery.data &&
                (teamScoresQuery.query.isLoading ?? true)
              }
              error={teamScoresQuery.query.error}
              charts={orgCharts}
              activeKey={chartKey}
              onChange={setChartKey}
            />
          )}
        </div>
        {organization ? (
          <div className="container-fluid">
            <OrgDetailTabs
              workspaceId={workspace.id}
              orgId={organization.id}
              assessmentKey={assessmentKey}
              isRubric={isRubric}
              views={detailViewConfig}
              pathname={location.pathname}
              search={location.search}
            />
          </div>
        ) : null}
      </PanelHeader>
      {!loading && !error && (
        <div className="content">
          <OrgAssessmentDetailContainer
            match={match}
            organization={organization}
            templates={templates}
            assessmentHistorySummaries={assessmentHistorySummaries}
          />
        </div>
      )}
      <SendRemindersModal
        orgName={reminderData.orgName}
        assessmentIds={reminderData.assessmentIds}
        isOpen={showConfirmSendReminders}
        onConfirm={handleSendReminders}
        onCancel={() => setShowConfirmReminders(false)}
      />
    </div>
  );
};

export default OrgComponent;
